import { ChangeEvent, FC, useCallback, createRef } from "react";
import styled from "@emotion/styled";
import { APP_BREAKPOINT } from "src/constants/layout";
import { useNavigate } from "react-router-dom";
import { useBlocNext } from "@blac/react";
import Translate from "src/ui/components/Translate/Translate";
import MealInsightBloc from "./MealInsightBloc";
import { PhotoIcon } from "src/constants/icons";
import { featureFlags } from "src/lib/featureFlags";
import { FeatureResponse } from "@9amhealth/openapi";
import { tracker } from "src/state/state";
import { isAndroidDevice } from "src/lib/platform";
import { intakeFile } from "src/lib/fileIntake/fileIntake";

const Card = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  background: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-top: 1.5rem;
  box-shadow: var(--light-shadow);

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    display: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

export const Badge = styled.span`
  background: linear-gradient(
    to right,
    rgba(83, 56, 158, 1),
    rgba(105, 65, 198, 1)
  );
  color: white;
  font-size: 9px;
  font-weight: 500;
  padding: 0.25rem 0.3125rem;
  border-radius: 0.25rem;
  line-height: 140%;
  text-transform: uppercase;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  border: 2px solid var(--secondary-dark-cream, rgba(242, 239, 231, 1));
  border-radius: 0.5rem;
  flex-shrink: 0;

  &:hover,
  &:focus,
  &:active {
    background: var(--secondary-dark-cream, rgba(242, 239, 231, 1));
  }
`;

const MealInsightUploadCard: FC = () => {
  const uploadInputRef = createRef<HTMLInputElement>();
  const [{ loading }, { handleUploadPhoto }] = useBlocNext(MealInsightBloc);
  const navigate = useNavigate();
  const featureFlagEnabled = featureFlags.getFlag(
    FeatureResponse.feature.MEAL_INSIGHTS
  );

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const rawFile = event.target.files?.[0];
    if (!rawFile) {
      return;
    }
    void intakeFile(rawFile).then((file) => {
      void handleUploadPhoto(file, navigate);
    });
  };

  const isAndroid = isAndroidDevice();

  const handleUploadButtonClick = useCallback(() => {
    tracker.track("Upload meal photo clicked");
    if (!loading) {
      uploadInputRef.current?.click();
    }
  }, [uploadInputRef, loading]);

  if (!featureFlagEnabled) {
    return null;
  }

  return (
    <Card>
      <Content>
        <TitleRow>
          <h4 className="as-body2">
            <Translate msg="mealInsights" />
          </h4>
          <Badge>
            <Translate msg="beta" />
          </Badge>
        </TitleRow>
        <p className="as-little" style={{ margin: "0 1rem 0 0" }}>
          <Translate msg="mealInsights.description" />
        </p>
      </Content>
      <IconContainer>
        <button
          id="file"
          onClick={handleUploadButtonClick}
          style={{ background: "transparent" }}
        >
          <PhotoIcon />
          <input
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            hidden
            capture={isAndroid ? "environment" : undefined}
            onChange={handleFileChange}
            ref={uploadInputRef}
            disabled={loading}
          />
        </button>
      </IconContainer>
    </Card>
  );
};

export default MealInsightUploadCard;
